import { Flex, Grid, GridProps, Skeleton } from '@chakra-ui/react';
import { LevelBar } from '../LevelBar/LevelBar';
import {
  IconFastTurns,
  IconHandling,
  IconLock,
  IconSpeed,
  IconStability,
  IconThrottle,
  IconUnlockedUpgrade,
  IconCooling,
  IconBraking,
  IconSlowTurns,
  IconSpeedStats,
  IconSpeedTunedup,
  IconFastturnsTunedup,
  IconHandlingTunedup,
  IconSlowturnsTunedup,
  IconCoolingTunedup,
  IconBreakingTunedup,
  IconAccelTunedup,
  IconStabilityTuning,
} from 'icons';

export interface StatValue {
  bars: number;
  tuneUpValidUntil?: string;
}

export interface CarStats {
  handling: StatValue;
  speed: StatValue;
  fastTurns: StatValue;
  slowTurns: StatValue;
  braking: StatValue;
  cooling: StatValue;
  acceleration: StatValue;
  stability: StatValue;
}

interface FusionCarStatsProps extends GridProps {
  stats: CarStats | null;
  carLevel: number;
  isLoading?: boolean;
  isLocked?: boolean;
  isCarStats?: boolean;
}

const LevelBarSkeleton = () => (
  <Skeleton
    height="18px"
    startColor="whiteAlpha.100"
    endColor="whiteAlpha.300"
    borderRadius="md"
  />
);

export const FusionCarStats = ({
  stats,
  isLoading = false,
  isLocked = false,
  carLevel,
  isCarStats = false,
  ...props
}: FusionCarStatsProps) => {
  if (isLoading || !stats) {
    return (
      <Grid
        templateColumns="repeat(3, 1fr)"
        templateRows="repeat(3, auto)"
        w="full"
        gap={4}
      >
        {[...Array(6)].map((_, index) => (
          <LevelBarSkeleton key={index} />
        ))}
      </Grid>
    );
  }

  return (
    <Grid
      pos="relative"
      templateColumns="repeat(4, 1fr)"
      templateRows="repeat(2, auto)"
      w="full"
      gap={{ base: 1, md: 1 }}
      paddingTop={isLocked ? '0.5rem' : '0'}
      paddingBottom={isLocked ? '0.5rem' : '0'}
      {...props}
    >
      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 3 : stats.speed.bars}
        title="Speed"
        icon={<IconSpeedStats __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        tunedUpIcon={<IconSpeedTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={isCarStats ? carLevel <= 1 : false}
        isTunedUp={!!stats.speed.tuneUpValidUntil}
      />


      <LevelBar 
        maxValue={6}
        currentValue={isLocked ? 3 : stats.fastTurns.bars}
        title="Fast Turns"
        icon={<IconFastTurns __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={isCarStats ? carLevel <= 1 : false}
        tunedUpIcon={<IconFastturnsTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.fastTurns.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 2 : stats.handling.bars}
        title="Handling"
        icon={<IconHandling __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 2}
        tunedUpIcon={<IconHandlingTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.handling.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 3 : stats.slowTurns.bars}
        title="Slow Turns"
        icon={<IconSlowTurns __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 3}
        tunedUpIcon={<IconSlowturnsTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.slowTurns.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 4 : stats.cooling.bars}
        title="Cooling"
        icon={<IconCooling __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 4}
        tunedUpIcon={<IconCoolingTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.cooling.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 5 : stats.braking.bars}
        title="Braking"
        icon={<IconBraking __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 4}
        tunedUpIcon={<IconBreakingTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.braking.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 5 : stats.acceleration.bars}
        title="Acceleration"
        icon={<IconThrottle __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 5}
        tunedUpIcon={<IconAccelTunedup w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isTunedUp={!!stats.acceleration.tuneUpValidUntil}
      />

      <LevelBar
        maxValue={6}
        currentValue={isLocked ? 5 : stats.stability.bars}
        title="Stability"
        icon={<IconStability __css={{ path: { fill: 'white.80' } }} w={{ base: '1rem', md: '1.25rem' }} h={{ base: '1rem', md: '1.25rem' }} />}
        isLockedStat={carLevel <= 5}
        isTunedUp={!!stats.stability.tuneUpValidUntil}
      />
    </Grid>
  );
};
